import React, { useMemo } from 'react'
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/format'
import { IncomeExpenseData } from '@/api/types'

interface IncomeExpenseChartProps {
  data: IncomeExpenseData | null
  branchId: number
  selectedYear: number
  isAccural: boolean
  onYearChange: (year: number) => void
  onAccountingMethodChange: (isAccural: boolean) => void
  className?: string
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

/**
 * Income and Expense chart with year selector and Accrual/Cash toggle
 */
export const IncomeExpenseChart: React.FC<IncomeExpenseChartProps> = ({
  data,
  selectedYear,
  isAccural,
  onYearChange,
  onAccountingMethodChange,
  className,
}) => {
  // Generate years for dropdown (current year and 5 years back)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => currentYear - i)
  }, [])

  const handleYearChange = (value: string) => {
    const year = parseInt(value, 10)
    onYearChange(year)
  }

  const handleAccountingMethodChange = (value: boolean) => {
    onAccountingMethodChange(value)
  }

  // Prepare chart data - handle 'expenses' vs 'expense' property and validate all values
  const chartData = useMemo(() => {
    console.log('📊 Chart received data:', data)
    
    if (!data || !data.monthlyIncExp || data.monthlyIncExp.length === 0) {
      console.log('📊 No monthly data available')
      return []
    }

    // Debug: Check first item structure
    if (data.monthlyIncExp.length > 0) {
      const firstItem = data.monthlyIncExp[0] as any
      console.log('📊 First item of monthlyIncExp:', firstItem)
      console.log('📊 Available keys in first item:', Object.keys(firstItem))
      console.log('📊 Has "expenses" property?', firstItem?.expenses !== undefined)
      console.log('📊 Has "expense" property?', firstItem?.expense !== undefined)
    }

    // EMERGENCY FIX: Sabhi NaN values ko 0 se replace karein
    const processed = data.monthlyIncExp
      .sort((a, b) => (a.month || 0) - (b.month || 0))
      .map((item) => {
        // Type assertion to handle API response with 'expenses' property
        const itemAny = item as any
        
        // Force convert to number and replace NaN with 0
        const income = Number(itemAny.income)
        // IMPORTANT: API se 'expenses' aa raha hai, 'expense' nahi
        const expenseValue = itemAny.expenses !== undefined ? itemAny.expenses : itemAny.expense
        const expense = Number(expenseValue)
        const month = Number(itemAny.month)
        const year = Number(itemAny.year) || selectedYear

        return {
          month: MONTHS[month - 1] || `Month ${month}`,
          monthNum: month,
          year: year,
          income: isFinite(income) && !isNaN(income) ? income : 0,
          expense: isFinite(expense) && !isNaN(expense) ? expense : 0,
        }
      })
      .filter(item => {
        // Extra safety check - ensure all values are valid
        const isValid = item.monthNum >= 1 && item.monthNum <= 12 && 
                       !isNaN(item.income) && !isNaN(item.expense) &&
                       isFinite(item.income) && isFinite(item.expense)
        
        if (!isValid) {
          console.warn('📊 Filtered out invalid month data:', item)
        }
        return isValid
      })

    // Final validation - ensure no NaN values in processed data
    const validated = processed.map(item => ({
      ...item,
      income: (typeof item.income === 'number' && isFinite(item.income) && !isNaN(item.income)) ? item.income : 0,
      expense: (typeof item.expense === 'number' && isFinite(item.expense) && !isNaN(item.expense)) ? item.expense : 0,
    }))

    console.log('📊 Processed chart data:', validated)
    console.log('📊 Chart data validation - any NaN?', validated.some(item => isNaN(item.income) || isNaN(item.expense)))
    return validated
  }, [data, selectedYear])

  // Calculate totals - use API data directly
  const totalIncome = data ? (typeof data.totalIncome === 'number' ? data.totalIncome : Number(data.totalIncome) || 0) : 0
  const totalExpense = data ? (typeof data.totalExpense === 'number' ? data.totalExpense : Number(data.totalExpense) || 0) : 0

  // Calculate safe max value for YAxis domain - ensure it's always a valid integer
  const maxValue = useMemo(() => {
    console.log('📊 Calculating maxValue from chartData:', chartData)
    
    if (!chartData || chartData.length === 0) {
      console.log('📊 No chart data, returning default 100')
      return 100
    }
    
    const allValues: number[] = []
    chartData.forEach((item, index) => {
      console.log(`📊 Item ${index}: income=${item.income}, expense=${item.expense}`)
      
      // Validate income
      if (typeof item.income === 'number') {
        if (!isNaN(item.income) && isFinite(item.income)) {
          allValues.push(Math.abs(item.income))
          console.log(`📊 Added income: ${Math.abs(item.income)}`)
        } else {
          console.warn(`📊 Invalid income at index ${index}: ${item.income}`)
        }
      }
      
      // Validate expense
      if (typeof item.expense === 'number') {
        if (!isNaN(item.expense) && isFinite(item.expense)) {
          allValues.push(Math.abs(item.expense))
          console.log(`📊 Added expense: ${Math.abs(item.expense)}`)
        } else {
          console.warn(`📊 Invalid expense at index ${index}: ${item.expense}`)
        }
      }
    })
    
    console.log('📊 All valid values:', allValues)
    
    if (allValues.length === 0) {
      console.log('📊 No valid values found, returning default 100')
      return 100
    }
    
    const max = Math.max(...allValues)
    console.log('📊 Max raw value:', max)
    
    // 20% padding add karein with minimum 100
    const paddedMax = Math.max(100, Math.ceil(max * 1.2))
    console.log('📊 Padded max value:', paddedMax)
    
    // Final validation
    if (!isFinite(paddedMax) || isNaN(paddedMax) || paddedMax <= 0) {
      console.warn('📊 Invalid padded max, returning 1000')
      return 1000
    }
    
    console.log('📊 Final maxValue:', paddedMax)
    return paddedMax
  }, [chartData])

  // Calculate safe YAxis domain with fallback
  const safeDomain = useMemo(() => {
    const safeMax = (maxValue > 0 && isFinite(maxValue) && !isNaN(maxValue)) ? maxValue : 1000
    const domain: [number, number] = [0, safeMax]
    console.log('📊 Safe domain calculated:', domain, 'maxValue:', maxValue)
    return domain
  }, [maxValue])

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border bg-background p-3 shadow-sm">
          <div className="space-y-2">
            <div className="font-semibold text-sm">{label}</div>
            <div className="space-y-1 text-xs">
              {payload.map((entry: any, index: number) => (
                <div key={index} className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    {entry.name}:
                  </span>
                  <span className="font-medium">{formatCurrency(entry.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  // Format Y-axis labels - ensure no NaN
  const formatYAxisLabel = (value: number) => {
    // Safety check for NaN or invalid values
    if (typeof value !== 'number' || !isFinite(value) || isNaN(value) || value < 0) {
      console.warn('📊 Invalid YAxis value in formatter:', value, 'type:', typeof value)
      return '0'
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`
    }
    return value.toString()
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Income and Expense</CardTitle>
            <CardDescription>
              Monthly income and expense overview
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <Select value={String(selectedYear)} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Accounting Method Toggle */}
            <div className="flex gap-1 border rounded-md p-1">
              <Button
                variant={isAccural ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleAccountingMethodChange(true)}
                className="h-8 px-3"
              >
                Accrual
              </Button>
              <Button
                variant={!isAccural ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleAccountingMethodChange(false)}
                className="h-8 px-3"
              >
                Cash
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!data || chartData.length === 0 || !isFinite(maxValue) || isNaN(maxValue) || maxValue <= 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No data available
          </div>
        ) : (
          <div className="space-y-4">
            {/* Chart */}
            {(() => {
              try {
                return (
                  <ResponsiveContainer width="100%" height={350}>
                    <RechartsBarChart
                      data={chartData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  tickFormatter={formatYAxisLabel}
                  // IMPORTANT FIX: Hardcoded domain with validation
                  domain={safeDomain}
                  allowDecimals={true}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="income"
                  name="Income"
                  fill="#0f172a"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="expense"
                  name="Expense"
                  fill="#64748b"
                  radius={[4, 4, 0, 0]}
                />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                )
              } catch (error) {
                console.error('📊 Chart rendering error:', error)
                return (
                  <div className="text-center py-8 text-red-500">
                    Chart rendering failed. Please check the data.
                  </div>
                )
              }
            })()}

            {/* Summary Totals */}
            <div className="flex items-center justify-center gap-8 pt-4 border-t">
              <div className="text-center">
                <div className="text-sm text-muted-foreground mb-1">Total Income</div>
                <div className="text-lg font-bold" style={{ color: '#0f172a' }}>
                  {formatCurrency(totalIncome)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-muted-foreground mb-1">Total Expense</div>
                <div className="text-lg font-bold" style={{ color: '#64748b' }}>
                  {formatCurrency(totalExpense)}
                </div>
              </div>
            </div>

            {/* Footnote */}
            <p className="text-xs text-muted-foreground text-center mt-2">
              * Income and expense values displayed are exclusive of taxes.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}


